import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    this.#watchDrawerMarkup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#markupObserver?.disconnect();
    this.#markupObserver = null;
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    const cartCount = this.#getCartCount(event);

    if (cartCount && cartCount > 0) {
      this.refs.dialog?.classList.remove('cart-drawer--empty');
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
    this.#queueDrawerStateSync(cartCount);
  };

  /**
   * Gets the current cart count from cart update events.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { itemCount?: number } }>} event
   */
  #getCartCount(event) {
    return event.detail.resource?.item_count ?? event.detail.data?.itemCount;
  }

  /**
   * Syncs the drawer classes after cart section morphs complete.
   * @param {number | undefined} cartCount
   */
  #queueDrawerStateSync(cartCount) {
    requestAnimationFrame(() => {
      this.#syncDrawerState(cartCount);
      requestAnimationFrame(() => this.#syncDrawerState(cartCount));
    });
  }

  /**
   * Keeps the drawer's empty state in step with its markup.
   *
   * The cart section is re-rendered after the cart changes, and the empty
   * layout is centred where the filled one is not. This used to be re-checked
   * on a 150ms timer: if the new markup arrived after the earlier checks, the
   * drawer painted in one layout and then shifted into the other a moment
   * later. Watching the markup instead means the state is set in the same
   * frame the content changes, so it only ever paints once.
   */
  #watchDrawerMarkup() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog || this.#markupObserver) return;

    this.#markupObserver = new MutationObserver(() => this.#syncDrawerState(undefined));
    this.#markupObserver.observe(dialog, { childList: true, subtree: true });
  }

  /** @type {MutationObserver | null} */
  #markupObserver = null;

  /**
   * Keeps the dialog state aligned with the live cart markup.
   * @param {number | undefined} cartCount
   */
  #syncDrawerState(cartCount) {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const hasFilledCart = Boolean(
      dialog.querySelector('.cart-form, .cart-drawer__summary, .cart-items__table-row')
    );
    const hasEmptyCart = Boolean(dialog.querySelector('.cart-drawer__content--empty'));
    const isEmpty = cartCount === 0 || (!hasFilledCart && hasEmptyCart);

    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
    this.#updateStickyState();
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
